export interface NotificationNavigationSource {
    TenantId: number;
    SubjectType: string;
    SubjectId: number;
    DefinitionCode?: string;
}

export interface NotificationNavigationTarget {
    commands: string[];
    queryParams: Record<string, string | number>;
    label: 'notificationInbox.open-record' | 'notificationInbox.open-list';
}

/** Only allow known internal destinations. Never derive identifiers from rendered notification text. */
export function notificationNavigationTarget(item: NotificationNavigationSource): NotificationNavigationTarget | null {
    if (!Number.isSafeInteger(item.TenantId) || item.TenantId < 0
        || !Number.isSafeInteger(item.SubjectId) || item.SubjectId <= 0) return null;
    const record = (path: string, queryParams: Record<string, string | number> = {}): NotificationNavigationTarget =>
        ({ commands: [path], queryParams, label: 'notificationInbox.open-record' });
    const list = (path: string, queryParams: Record<string, string | number> = {}): NotificationNavigationTarget =>
        ({ commands: [path], queryParams, label: 'notificationInbox.open-list' });
    // Business records require an explicit tenant, never the platform-wide workspace.
    if (item.TenantId === 0 && item.SubjectType !== 'AlertIncident') return null;
    switch (item.SubjectType) {
        case 'Shipment':
        case 'ShipmentOriginHandover': return record(`/shipment/shipment_result/${item.SubjectId}`);
        case 'Wallet':
            // Historical notifications retain the wallet ID only. Open its queue, never guess an order.
            return ['WALLET_TOPUP_REQUESTED', 'WALLET_TOPUP_APPROVED', 'WALLET_TOPUP_REJECTED',
                'WALLET_TOPUP_REVIEW_OVERDUE', 'WALLET_TOPUP_REVIEW_ESCALATED'].includes(item.DefinitionCode ?? '')
                ? list('/systems/wallet', { walletInfoId: item.SubjectId, topupQueue: 1 })
                : list('/systems/wallet');
        case 'SellingPriceBook': return record(`/services/pricing/customer/${item.SubjectId}`);
        case 'VendorCostPriceBook': return record(`/services/pricing/vendor/${item.SubjectId}`);
        case 'VendorService': return record('/services/core_logistics_service/vendor-health', { vendorServiceId: item.SubjectId });
        case 'AlertIncident': return record('/systems/notification', { tab: 'incidents', id: item.SubjectId });
        case 'ReportSchedule': return list('/reports/admin/export-jobs');
        case 'PickupBooking': return list('/dispatch/pickup-booking');
        case 'PickupNotice': return list('/dispatch/pickup-notice');
        case 'ShipmentWarehouseInspection': return list('/dispatch/warehouse-inspection');
        case 'VendorPickupRegistration': return list('/services/vendors/pickup-registration');
        case 'FinancialReconciliationStatement': return record(`/billing/financial_reconciliation/${item.SubjectId}`);
        case 'CommissionPayoutBatch': return list('/commission/commission_payout_batch');
        case 'CommissionAlert': return list('/commission/commission_reconciliation');
        case 'ShipmentManagementPriceActivation':
        case 'VendorManagementPriceActivation': return list('/services/pricing');
        case 'UsageAlert': return record('/workspace/usage', { usageAlertId: item.SubjectId });
        case 'Receivable': return record('/services/receivable', { receivableId: item.SubjectId });
        case 'CustomerBillingStatement': return record('/services/receivable/statements', { statementId: item.SubjectId });
        case 'DailyBalanceSnapshot': return record(`/systems/balances/snapshot/${item.SubjectId}`);
        default: return null;
    }
}
